/**
 * PDP CROSS-SELL — Yotpo star ratings for Trending Beauty / Recently Viewed
 *
 * Both sections inject their cards after render (product-recommendations fetches over AJAX,
 * recently-viewed-products swaps in the search results), and the reviews.rating metafield the
 * theme reads server-side is empty for this store, so the stars cannot come from Liquid. Each
 * card leaves a [data-yotpo-rating] placeholder carrying its product id; this reads the store's
 * bottom line from the Yotpo API and draws the stars once the cards land.
 *
 * Lifted out of sections/related-products.liquid so Recently Viewed gets the same treatment
 * from one copy instead of two inline scripts drifting apart.
 */
(() => {
  const SECTION_SELECTOR =
    '.shopify-section--related-products, .shopify-section--recently-viewed-products, .slider-trending-section';
  const HOST_SELECTOR = 'product-recommendations, recently-viewed-products, slider-trending';

  /**
   * Yotpo store keys — read off the loader scripts so they survive a key rotation.
   *
   * The store loads Yotpo twice, reviews and loyalty, each with its own key, and the order the two
   * loaders land in is not stable. Taking the first one silently asked the loyalty account for
   * review counts and every call came back 404, which is why no card ever drew stars. So collect
   * every key and let the probe below decide which one answers.
   */
  const getAppKeys = () => {
    const keys = [...document.querySelectorAll('script[src*="widgetsrepository.yotpo.com/v1/loader/"]')]
      .map((loader) => (loader.src.match(/loader\/([^?/]+)/) || [])[1])
      .filter(Boolean);
    return [...new Set(keys)];
  };

  const reviewsUrl = (appKey, productId) =>
    'https://api-cdn.yotpo.com/v1/widget/' + appKey + '/products/' + productId + '/reviews.json';

  const fetchBottomline = (appKey, productId) =>
    fetch(reviewsUrl(appKey, productId))
      .then((response) => (response.ok ? response.json() : null))
      .then((payload) => (payload && payload.response ? payload.response.bottomline : null))
      .catch(() => null);

  /* Probed once per page and shared by both sections: the key that answers is the reviews account. */
  let appKeyPromise = null;
  const resolveAppKey = (appKeys, productId) => {
    if (!appKeyPromise) {
      appKeyPromise = appKeys.reduce(
        (chain, appKey) =>
          chain.then(
            (found) =>
              found || fetchBottomline(appKey, productId).then((bottomline) => (bottomline ? appKey : null))
          ),
        Promise.resolve(null)
      );
    }
    return appKeyPromise;
  };

  const drawStars = (element, score, total) => {
    if (!total || total <= 0 || !score) return; // No reviews → draw nothing.

    const percentage = Math.max(0, Math.min(100, (score / 5) * 100));
    const rounded = Math.round(score * 10) / 10;

    element.innerHTML =
      '<span class="product-card__stars" aria-hidden="true">' +
      '<span class="product-card__stars-bg">★★★★★</span>' +
      '<span class="product-card__stars-fill" style="width:' +
      percentage +
      '%">★★★★★</span>' +
      '</span>' +
      '<span class="product-card__stars-num">' +
      rounded +
      '</span>';
    element.setAttribute('role', 'img');
    element.setAttribute(
      'aria-label',
      rounded + ' out of 5 stars, ' + total + ' review' + (total === 1 ? '' : 's')
    );
  };

  const render = (host) => {
    const placeholders = [...host.querySelectorAll('[data-yotpo-rating]:not([data-rated])')];
    if (placeholders.length === 0) return; // Cards not injected yet — the observer will call back.

    const appKeys = getAppKeys();
    if (appKeys.length === 0) {
      setTimeout(() => render(host), 300); // Yotpo loaders not on the page yet — retry.
      return;
    }

    /* Claimed up front so a burst of mutations does not fetch the same card twice. */
    placeholders.forEach((element) => element.setAttribute('data-rated', ''));

    resolveAppKey(appKeys, placeholders[0].getAttribute('data-product-id')).then((appKey) => {
      if (!appKey) return;

      placeholders.forEach((element) => {
        const productId = element.getAttribute('data-product-id');
        if (!productId) return;

        fetchBottomline(appKey, productId).then((bottomline) => {
          if (bottomline) drawStars(element, bottomline.average_score, bottomline.total_review);
        });
      });
    });
  };

  const observe = (host) => {
    new MutationObserver(() => render(host)).observe(host, { childList: true, subtree: true });
    render(host); // In case the cards are already there (instant navigation / cached render).
  };

  /**
   * PDP RATING ROW (snippets/product-info.liquid, Figma node 1733:7410 "Frame 4")
   *
   * Same bottom-line data as the cards above, drawn into the main product's rating row: stars +
   * score, the review count, and a click that scrolls to the Yotpo reviews section. The row ships
   * hidden and only unhides once Yotpo answers, so a product the reviews account doesn't know
   * about never shows a half-empty row.
   */
  const startPdpRating = () => {
    const row = document.querySelector('[data-pdp-rating]');
    if (!row) return;

    const starsElement = row.querySelector('[data-yotpo-rating]');
    const productId = starsElement && starsElement.getAttribute('data-product-id');
    if (!productId) return;

    const appKeys = getAppKeys();
    if (appKeys.length === 0) {
      setTimeout(startPdpRating, 300); // Yotpo loaders not on the page yet — retry.
      return;
    }

    resolveAppKey(appKeys, productId).then((appKey) => {
      if (!appKey) return;

      fetchBottomline(appKey, productId).then((bottomline) => {
        if (!bottomline) return;

        const total = bottomline.total_review || 0;
        drawStars(starsElement, bottomline.average_score, total);

        const countElement = row.querySelector('[data-yotpo-review-count]');
        if (countElement) {
          countElement.textContent = total.toLocaleString('en-US') + ' Review' + (total === 1 ? '' : 's');
        }

        row.hidden = false;
      });
    });

    row.addEventListener('click', (event) => {
      /* The reviews widget is an app block, so its section id isn't known at build time — find it live.
         Two separate queries, not one selector list: the mega menu (snippets/mega-menu-products.liquid)
         renders its own .yotpo-widget-instance in the header, and a combined selector returns whichever
         comes first in document order — the header one, which "scrolled" to where the visitor already was.
         The reviews widget is the one inside the apps section. */
      const widget =
        document.querySelector('#yotpo-reviews-main-widget') ||
        document.querySelector('.shopify-section--apps .yotpo-widget-instance');
      if (!widget) return; // No reviews section on the page — the href="#" click stays inert.

      event.preventDefault();
      const target = widget.closest('.shopify-section') || widget;
      /* Keeps the section clear of the sticky header when the scroll lands. */
      target.style.scrollMarginBlockStart = 'calc(var(--sticky-area-height, 0px) + 20px)';
      target.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  };

  const start = () => {
    document.querySelectorAll(SECTION_SELECTOR).forEach((section) => {
      const host = section.querySelector(HOST_SELECTOR);
      if (host) observe(host);
    });

    startPdpRating();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }
})();
